/**
 * Platform Admin · Manage Users (Slice 2)
 *
 * Browse, search and delete users belonging to a single school.
 * Reached via /platform-admin/schools/:school_id/users.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Trash2, Loader2, Pencil, Mail } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLE_LABELS = {
  school_admin: "Admins",
  teacher: "Teachers",
  student: "Students",
  parent: "Parents",
};

export default function PlatformAdminUsers() {
  const { school_id } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);   // user obj
  const [deletingId, setDeletingId] = useState(null);
  const [editUser, setEditUser] = useState(null);             // user obj being edited
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const [editSendInvite, setEditSendInvite] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [schoolRes, listRes] = await Promise.all([
        api.get(`/schools/${school_id}`),
        api.get(`/schools/${school_id}/users`, {
          params: { role: roleFilter || undefined, q: search || undefined, limit: 200 },
        }),
      ]);
      setSchool(schoolRes.data);
      setUsers(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [school_id, roleFilter]);

  const filtered = useMemo(() => users, [users]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.user_id);
    try {
      const { data } = await api.delete(`/users/${confirmDelete.user_id}`);
      toast.success(
        `Deleted ${confirmDelete.email || confirmDelete.user_id}` +
        (data.counts?.children ? ` · ${data.counts.children} child profile(s) removed` : ""),
      );
      setUsers((prev) => prev.filter((u) => u.user_id !== confirmDelete.user_id));
      setTotal((t) => t - 1);
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({ name: user.name || "", email: user.email || "" });
    setEditSendInvite(false);
  };

  const emailChanged = editUser && editForm.email.toLowerCase() !== (editUser.email || "").toLowerCase();

  const handleEdit = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      const payload = {};
      if (editForm.name.trim() !== (editUser.name || "")) payload.name = editForm.name.trim();
      if (editForm.email.trim().toLowerCase() !== (editUser.email || "").toLowerCase()) {
        payload.email = editForm.email.trim();
        if (editSendInvite) payload.send_invite = true;
      }
      if (!Object.keys(payload).length) {
        toast.info("No changes to save.");
        setEditUser(null);
        return;
      }
      const { data } = await api.patch(`/users/${editUser.user_id}`, payload);
      toast.success(
        `Updated ${editForm.name || editUser.email}` +
        (data.invite_sent ? " · Notification email sent" : ""),
      );
      // Update local state
      setUsers((prev) => prev.map((u) =>
        u.user_id === editUser.user_id
          ? { ...u, ...data.changes }
          : u
      ));
      setEditUser(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update user");
    } finally {
      setEditSaving(false);
    }
  };

  if (loading && !school) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate("/platform-admin")}
            className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm"
            data-testid="manage-users-back"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div className="border-l border-slate-200 pl-3 ml-2">
            <h1 className="font-display font-bold text-lg" data-testid="manage-users-school-name">
              {school?.name} · Manage Users
            </h1>
            <div className="text-xs text-slate-500 font-mono">{total} users in this school</div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="manage-users-filters">
          {["", "school_admin", "teacher", "student", "parent"].map((r) => (
            <Button
              key={r || "all"}
              variant={roleFilter === r ? "default" : "outline"}
              size="sm"
              onClick={() => setRoleFilter(r)}
              data-testid={`role-filter-${r || "all"}`}
            >
              {r ? ROLE_LABELS[r] : "All"}
            </Button>
          ))}
          <form onSubmit={handleSearch} className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search email or name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 w-64"
                data-testid="manage-users-search-input"
              />
            </div>
            <Button type="submit" size="sm" variant="outline" data-testid="manage-users-search-submit">
              Search
            </Button>
          </form>
        </div>

        {/* User table */}
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Role</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && !loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-slate-400" data-testid="manage-users-empty">
                    No users match.
                  </td>
                </tr>
              ) : filtered.map((u) => (
                <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{u.email || "—"}</td>
                  <td className="px-4 py-3 capitalize">{u.role.replace("_", " ")}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(u)}
                      className="text-slate-600 border-slate-200 hover:bg-slate-50"
                      data-testid={`user-edit-${u.user_id}`}
                    >
                      <Pencil size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmDelete(u)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      disabled={deletingId === u.user_id}
                      data-testid={`user-delete-${u.user_id}`}
                    >
                      <Trash2 size={14} className="mr-1" />
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={confirmDelete !== null} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <DialogContent className="max-w-md" data-testid="user-delete-dialog">
          {confirmDelete && (
            <>
              <DialogHeader>
                <DialogTitle>Delete {confirmDelete.email || confirmDelete.name || "this user"}?</DialogTitle>
                <DialogDescription>
                  This permanently removes the user, all linked child profiles, chat history,
                  assessment attempts, sessions, and any pending invites. <b>This cannot be undone.</b>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(null)}
                  disabled={deletingId === confirmDelete.user_id}
                  data-testid="user-delete-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={deletingId === confirmDelete.user_id}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="user-delete-confirm"
                >
                  {deletingId === confirmDelete.user_id ? "Deleting…" : "Delete forever"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUser !== null} onOpenChange={(v) => { if (!v) setEditUser(null); }}>
        <DialogContent className="max-w-md" data-testid="user-edit-dialog">
          {editUser && (
            <>
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
                <DialogDescription>
                  Update details for {editUser.email || editUser.name}
                  <span className="block mt-1 text-xs capitalize text-slate-400">
                    Role: {editUser.role?.replace("_", " ")}
                  </span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Full name"
                    className="mt-1"
                    data-testid="user-edit-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="email@example.com"
                    className="mt-1"
                    data-testid="user-edit-email"
                  />
                </div>
                {emailChanged && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <Checkbox
                      id="send-invite"
                      checked={editSendInvite}
                      onCheckedChange={(v) => setEditSendInvite(!!v)}
                      data-testid="user-edit-send-invite"
                    />
                    <label htmlFor="send-invite" className="text-sm text-blue-800 leading-tight cursor-pointer">
                      <Mail size={14} className="inline mr-1 -mt-0.5" />
                      Send notification email to the new address
                    </label>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setEditUser(null)}
                  disabled={editSaving}
                  data-testid="user-edit-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEdit}
                  disabled={editSaving}
                  data-testid="user-edit-save"
                >
                  {editSaving ? "Saving…" : "Save Changes"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
